import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AppLayout } from './components/layout/AppLayout'
import { RouteAnalytics } from './lib/analytics/RouteAnalytics'
import { isFirebaseConfigured } from './lib/firebase'
import { useAuth } from './lib/auth/useAuth'

// Each page is its own chunk, loaded on demand — keeps the initial bundle small.
const HomePage = lazy(() =>
  import('./pages/HomePage').then((m) => ({ default: m.HomePage })),
)
const HistoryPage = lazy(() =>
  import('./pages/HistoryPage').then((m) => ({ default: m.HistoryPage })),
)
const LandingPage = lazy(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })),
)
const SignInPage = lazy(() =>
  import('./pages/SignInPage').then((m) => ({ default: m.SignInPage })),
)
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
)
const TermsPage = lazy(() =>
  import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })),
)

function Fallback() {
  return (
    <div className="flex min-h-svh items-center justify-center text-ink-muted">
      Loading…
    </div>
  )
}

function AuthedApp() {
  return (
    <BrowserRouter>
      <RouteAnalytics />
      <Suspense fallback={<Fallback />}>
        <Routes>
          {/* Legal pages render standalone, without the app chrome. */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route
            path="*"
            element={
              <AppLayout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/history" element={<HistoryPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppLayout>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

function PublicApp() {
  return (
    <BrowserRouter>
      <RouteAnalytics />
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

function App() {
  const { user, loading } = useAuth()

  // No Firebase config → run local-only (no sign-in gate, localStorage persistence).
  if (!isFirebaseConfigured) return <AuthedApp />

  if (loading) {
    return (
      <div className="flex min-h-svh items-center justify-center text-ink-muted">
        Loading…
      </div>
    )
  }
  if (!user) return <PublicApp />
  return <AuthedApp />
}

export default App
